import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, ImageIcon, Loader2, Paperclip, Sparkles, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getExamesControllerListarQueryKey,
  useExamesControllerCriarExterno,
} from '@/api/generated/exames/exames';
import { aplicarErrosDoServidor } from '@/api/erros';
import type { ExameResponse, ExtracaoDeExameResponse } from '@/api/generated/model';
import { RevisaoDeValores } from '@/features/exames/components/RevisaoDeValores';
import {
  campoDeHorasAposMedicacao,
  DICA_HORAS_APOS_MEDICACAO,
  horasParaEnvio,
} from '@/features/exames/horasAposMedicacao';
import { dataParaISO } from '@/features/exames/rotulos';
import {
  chaveDoArquivo,
  useAnexosComLeitura,
  type Anexo,
} from '@/features/exames/useAnexosComLeitura';
import {
  linhasDaLeitura,
  mesclarLinhas,
  validarValores,
  valoresParaEnvio,
  type ErrosDosValores,
  type ValorDoLaudo,
} from '@/features/exames/valoresDoLaudo';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { ComboBox } from '@/shared/ui/combobox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { toast } from '@/shared/ui/sonner';
import { Switch } from '@/shared/ui/switch';
import { Textarea } from '@/shared/ui/textarea';

/** Espelha o perfil `exame-anexo` do backend. Ver `infra/storage/perfis.ts`. */
const FORMATOS = 'application/pdf,image/png,image/jpeg,image/webp';
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_ANEXOS = 10;

/**
 * Atalho para os exames de imagem mais comuns.
 *
 * O `permitirLivre` do ComboBox é o que importa: a lista é o caminho curto
 * para quatro casos, não uma allowlist — "densitometria" e "cortisol basal"
 * precisam caber. Escolher um destes liga o "Exame de imagem" sozinho.
 */
const NOMES_DE_IMAGEM = ['Ultrassom', 'Tomografia', 'Cintilografia', 'Ressonância magnética'];
const EXAMES_DE_IMAGEM = NOMES_DE_IMAGEM.map((nome) => ({ valor: nome, rotulo: nome }));

const schema = z.object({
  nome: z.string().trim().min(1, 'Informe o nome do exame').max(200),
  realizadoEm: z.string().min(1, 'Informe a data em que o exame foi realizado'),
  laboratorio: z.string().trim().max(120),
  metodoAnalitico: z.string().trim().max(120, 'No máximo 120 caracteres'),
  horasAposMedicacao: campoDeHorasAposMedicacao,
  observacoes: z.string().trim().max(2000),
  imagem: z.boolean(),
  laudoTexto: z.string().trim().max(10_000, 'No máximo 10.000 caracteres'),
});

type Formulario = z.infer<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  pacienteId: string;
  /**
   * Chamado com o exame recém-criado, antes de fechar. É como o prontuário e o
   * anexo (item 16) vinculam na hora o exame que se cadastrou de dentro deles.
   */
  aoCadastrar?: (exame: ExameResponse) => void;
}

/**
 * Cadastro de exame **externo** — o que a pessoa tem em PDF ou em foto, feito
 * fora da plataforma.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  O documento é o ponto de partida: ele sobe NA ESCOLHA, a leitura automática
 *  preenche nome, data, laboratório, método e os valores, e a pessoa confere
 *  antes de cadastrar. Os valores ligados a um analito do catálogo viram
 *  resultado e entram no gráfico de evolução do paciente.
 *
 *  ⚠️ A leitura PROPÕE, e só. Ela nunca sobrescreve o que a pessoa digitou
 *  (só preenche campo vazio), nada é gravado antes do "Cadastrar", e sem IA
 *  no ambiente a tela funciona igual — preenchida à mão.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **Não é o `ExameFormDialogo` com um campo a mais.** Aquele abre um exame
 * do PORTFÓLIO, que define os analitos a lançar depois e passa pelas etapas do
 * laboratório. Este registra o que já foi feito em outro lugar: o nome é texto
 * livre, não há status (já nasce concluído), e os valores apontam para
 * analitos de qualquer item do catálogo.
 */
export function ExameExternoDialogo({ aberto, aoFechar, pacienteId, aoCadastrar }: Props) {
  const queryClient = useQueryClient();
  const criar = useExamesControllerCriarExterno();

  const [valores, setValores] = useState<ValorDoLaudo[]>([]);
  const [errosDosValores, setErrosDosValores] = useState<ErrosDosValores>({});
  /** Alguma leitura já preencheu algo — a tela pede a conferência. */
  const [preenchidoPelaLeitura, setPreenchidoPelaLeitura] = useState(false);
  /** O nome do animal impresso no laudo, quando NÃO é o do paciente. */
  const [outroPaciente, setOutroPaciente] = useState<string | null>(null);
  /**
   * A pessoa mexeu no "Exame de imagem" com a própria mão. A partir daí nem a
   * leitura nem o nome escolhido mudam mais o interruptor: a decisão é dela.
   */
  const imagemDecidida = useRef(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  const ehImagem = watch('imagem') ?? false;

  /**
   * Junta uma leitura ao formulário.
   *
   * ⚠️ Só preenche o que está VAZIO. São vários documentos por exame, lidos um
   * a um, e a pessoa pode ter digitado antes de a leitura voltar: a segunda
   * página não pode trocar a data que a primeira trouxe, nem a IA o nome que a
   * pessoa escreveu.
   */
  function aplicarLeitura(leitura: ExtracaoDeExameResponse) {
    const atual = getValues();
    const preencher = (
      campo: 'nome' | 'realizadoEm' | 'laboratorio' | 'metodoAnalitico',
      valor: string | null,
    ) => {
      if (valor && !atual[campo]?.trim())
        setValue(campo, valor, { shouldDirty: true, shouldValidate: true });
    };

    preencher('nome', leitura.nome);
    preencher('realizadoEm', leitura.realizadoEm);
    preencher('laboratorio', leitura.laboratorio);
    preencher('metodoAnalitico', leitura.metodoAnalitico);

    if (leitura.tipo === 'IMAGEM') {
      if (!imagemDecidida.current) setValue('imagem', true, { shouldDirty: true });

      // Laudo de imagem em várias páginas: o texto da segunda continua o da
      // primeira, em vez de substituí-lo.
      const texto = leitura.laudoTexto?.trim();
      const jaTem = atual.laudoTexto?.trim() ?? '';
      if (texto && !jaTem.includes(texto)) {
        setValue('laudoTexto', jaTem ? `${jaTem}\n\n${texto}` : texto, { shouldDirty: true });
      }
    }

    setValores((atuais) => mesclarLinhas(atuais, linhasDaLeitura(leitura)));
    setErrosDosValores({});
    setPreenchidoPelaLeitura(true);
    if (leitura.pacienteConfere === false) setOutroPaciente(leitura.pacienteNoDocumento);
  }

  const documentos = useAnexosComLeitura({
    pacienteId,
    aoLer: aplicarLeitura,
    aoFalharOEnvio: (_arquivo, mensagem) => toast.error(mensagem),
  });
  const { reiniciar: reiniciarDocumentos } = documentos;

  useEffect(() => {
    if (!aberto) return;
    reset({
      nome: '',
      realizadoEm: '',
      laboratorio: '',
      metodoAnalitico: '',
      horasAposMedicacao: '',
      observacoes: '',
      imagem: false,
      laudoTexto: '',
    });
    setValores([]);
    setErrosDosValores({});
    setPreenchidoPelaLeitura(false);
    setOutroPaciente(null);
    imagemDecidida.current = false;
    reiniciarDocumentos();
  }, [aberto, reset, reiniciarDocumentos]);

  // A data de realização é passado: um exame que "foi feito" amanhã não existe.
  const hoje = new Date().toISOString().slice(0, 10);

  function adicionarArquivos(escolhidos: FileList | null) {
    if (!escolhidos || escolhidos.length === 0) return;

    const novos = [...escolhidos];
    const grande = novos.find((a) => a.size > MAX_BYTES);
    if (grande) {
      toast.error(`"${grande.name}" passa de 10 MB.`);
      return;
    }

    // Compara por nome+tamanho porque `File` não tem identidade estável: quem
    // reabre o seletor e escolhe o mesmo laudo receberia dois objetos
    // diferentes para o mesmo documento.
    const existentes = new Set(documentos.anexos.map((anexo) => anexo.chave));
    const ineditos = novos.filter((arquivo) => !existentes.has(chaveDoArquivo(arquivo)));
    const vagas = MAX_ANEXOS - documentos.anexos.length;

    if (ineditos.length > vagas) toast.error(`No máximo ${MAX_ANEXOS} documentos por exame.`);
    documentos.adicionar(ineditos.slice(0, Math.max(vagas, 0)));
  }

  const aoEnviar = handleSubmit(async (dados) => {
    // Exame de imagem sem linha nenhuma não tem valor a validar; com linhas (a
    // pessoa digitou uma medida), elas valem como em qualquer exame.
    const erros = validarValores(valores);
    setErrosDosValores(erros);
    if (Object.keys(erros).length > 0) return;

    const { resultados, resultadosSemAnalito } = valoresParaEnvio(valores);
    const arquivoIds = documentos.anexos.flatMap((anexo) =>
      anexo.arquivoId ? [anexo.arquivoId] : [],
    );

    try {
      const exame = await criar.mutateAsync({
        data: {
          pacienteId,
          nome: dados.nome.trim(),
          realizadoEm: dataParaISO(dados.realizadoEm),
          laboratorio: dados.laboratorio.trim() || null,
          metodoAnalitico: dados.metodoAnalitico.trim() || null,
          horasAposMedicacao: horasParaEnvio(dados.horasAposMedicacao),
          observacoes: dados.observacoes.trim() || null,
          imagem: dados.imagem,
          // O texto do laudo só existe na tela com o interruptor ligado — e o
          // que não está na tela não é enviado.
          laudoTexto: dados.imagem ? dados.laudoTexto.trim() || null : null,
          ...(arquivoIds.length > 0 ? { arquivoIds } : {}),
          ...(resultados.length > 0 ? { resultados } : {}),
          ...(resultadosSemAnalito.length > 0 ? { resultadosSemAnalito } : {}),
        },
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getExamesControllerListarQueryKey() }),
        // O resumo e as séries do paciente: é o que faz o valor recém-cadastrado
        // aparecer na aba Evolução sem recarregar a página.
        queryClient.invalidateQueries({
          predicate: (q) =>
            String(q.queryKey[0]).startsWith(`/v1/historico/pacientes/${pacienteId}`),
        }),
      ]);
      toast.success('Exame externo cadastrado.');
      aoCadastrar?.(exame);
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível cadastrar o exame.');
    }
  });

  const ocupado = isSubmitting || documentos.enviando || documentos.lendo;
  // Com o interruptor ligado a tabela some — a menos que já tenha linhas: o
  // que está preenchido não pode ser enviado sem estar à vista.
  const mostrarValores = !ehImagem || valores.length > 0;

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cadastrar exame externo</DialogTitle>
        </DialogHeader>

        {/*
          ⚠️ `stopPropagation`: este diálogo também abre de DENTRO do formulário
          do prontuário e do anexo (item 16, "Cadastrar exame externo"). O submit
          sintético do React atravessa o portal e subiria até o formulário de
          fora — que salvava o anexo (irreversível) antes de o exame ser vinculado.
        */}
        <form
          onSubmit={(e) => {
            e.stopPropagation();
            void aoEnviar(e);
          }}
          className="space-y-4"
        >
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          {/* Os documentos vêm PRIMEIRO: é deles que o resto do formulário sai. */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-brand">Documentos</p>

            {documentos.anexos.length > 0 && (
              <ul className="space-y-1.5">
                {documentos.anexos.map((anexo) => (
                  <LinhaDoDocumento
                    key={anexo.chave}
                    anexo={anexo}
                    aoRemover={() => documentos.remover(anexo.chave)}
                    aoCancelarLeitura={() => documentos.cancelarLeitura(anexo.chave)}
                    aoLerDeNovo={() => documentos.lerDeNovo(anexo.chave)}
                    podeLer={!documentos.leituraIndisponivel}
                  />
                ))}
              </ul>
            )}

            {documentos.anexos.length < MAX_ANEXOS && (
              <label className="flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-brand/30 py-6 cursor-pointer hover:border-brand/50">
                <input
                  type="file"
                  multiple
                  accept={FORMATOS}
                  className="hidden"
                  aria-label="Anexar documento do exame"
                  onChange={(e) => {
                    adicionarArquivos(e.target.files);
                    // Zera o input: sem isto, escolher o mesmo arquivo depois
                    // de removê-lo não dispara `change` de novo.
                    e.target.value = '';
                  }}
                />
                <Paperclip className="w-5 h-5 text-brand" aria-hidden="true" />
                <span className="text-sm font-medium text-brand">
                  {documentos.anexos.length === 0
                    ? 'Clique para anexar o laudo (PDF ou imagem)'
                    : 'Anexar outro documento'}
                </span>
                <span className="text-xs text-muted-foreground">
                  PDF, PNG, JPEG ou WebP — até 10 MB cada. O laudo é lido e os campos abaixo são
                  preenchidos para você conferir.
                </span>
              </label>
            )}
          </div>

          {documentos.leituraIndisponivel && (
            <Alert>
              <AlertDescription>
                A leitura automática não está disponível neste ambiente. O documento fica anexado —
                preencha os campos à mão.
              </AlertDescription>
            </Alert>
          )}

          {outroPaciente && (
            <Alert variant="destructive">
              <AlertDescription>
                O documento traz o nome <strong>{outroPaciente}</strong>, que não é o deste
                paciente. Confira se o laudo é do animal certo antes de cadastrar.
              </AlertDescription>
            </Alert>
          )}

          {preenchidoPelaLeitura && (
            <Alert>
              <AlertDescription className="flex items-start gap-2">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                <span>
                  Campos preenchidos pela leitura do documento. Confira cada um com o laudo antes de
                  cadastrar — a leitura pode errar.
                </span>
              </AlertDescription>
            </Alert>
          )}

          <Campo
            id="nome"
            rotulo="Exame"
            obrigatorio
            erro={errors.nome?.message}
            dica="Escolha um da lista ou digite o nome que estiver no laudo."
          >
            <Controller
              name="nome"
              control={control}
              render={({ field }) => (
                <ComboBox
                  id="nome"
                  aria-label="Exame"
                  valor={field.value ?? ''}
                  // O nome digitado não está em `opcoes`, e sem isto o gatilho
                  // voltaria a mostrar o placeholder depois de escolhido.
                  rotuloSelecionado={field.value ?? ''}
                  aoEscolher={(nome) => {
                    field.onChange(nome);
                    if (!imagemDecidida.current && NOMES_DE_IMAGEM.includes(nome)) {
                      setValue('imagem', true, { shouldDirty: true });
                    }
                  }}
                  opcoes={EXAMES_DE_IMAGEM}
                  permitirLivre
                  placeholder="Selecione o exame"
                  placeholderBusca="Selecione o exame ou digite..."
                  className={CLASSE_CONTROLE}
                />
              )}
            />
          </Campo>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Campo
              id="realizadoEm"
              rotulo="Data de realização"
              obrigatorio
              erro={errors.realizadoEm?.message}
            >
              <Input
                id="realizadoEm"
                type="date"
                max={hoje}
                className={CLASSE_CONTROLE}
                {...register('realizadoEm')}
              />
            </Campo>

            <Campo
              id="laboratorio"
              rotulo="Laboratório"
              dica="Opcional."
              erro={errors.laboratorio?.message}
            >
              <Input
                id="laboratorio"
                placeholder="Onde foi feito"
                className={CLASSE_CONTROLE}
                {...register('laboratorio')}
              />
            </Campo>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* ⚠️ O método mora no CATÁLOGO — é propriedade do ensaio que o
                laboratório roda, não do exame. Este campo só existe porque o
                laudo veio no papel timbrado de OUTRO laboratório, e é override
                daquele. Preenchido em todo exame, o filtro por método da
                Análise Comparativa passa a ter "RIE", "R.I.E." e
                "radioimunoensaio" como três métodos diferentes. */}
            <Campo
              id="metodoAnalitico"
              rotulo="Método analítico"
              dica="Só se estiver escrito no laudo de fora. Nos exames do portfólio, o método vem do catálogo."
              erro={errors.metodoAnalitico?.message}
            >
              <Input
                id="metodoAnalitico"
                placeholder="Ex.: Quimioluminescência"
                className={CLASSE_CONTROLE}
                {...register('metodoAnalitico')}
              />
            </Campo>

            <Campo
              id="horasAposMedicacao"
              rotulo="Horas após a medicação"
              dica={DICA_HORAS_APOS_MEDICACAO}
              erro={errors.horasAposMedicacao?.message}
            >
              <Input
                id="horasAposMedicacao"
                inputMode="decimal"
                placeholder="Ex.: 4,5"
                className={CLASSE_CONTROLE}
                {...register('horasAposMedicacao')}
              />
            </Campo>
          </div>

          <Controller
            name="imagem"
            control={control}
            render={({ field }) => (
              <div className="flex items-start gap-3 rounded-md border border-brand/20 px-3 py-2.5">
                <Switch
                  id="imagem"
                  checked={field.value ?? false}
                  onCheckedChange={(ligado) => {
                    imagemDecidida.current = true;
                    field.onChange(ligado);
                  }}
                  className="mt-0.5"
                />
                <div className="space-y-0.5">
                  <Label htmlFor="imagem" className="text-brand font-medium">
                    Exame de imagem
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Ultrassom, raio-x, tomografia e afins: em vez de valores, o laudo é um texto.
                  </p>
                </div>
              </div>
            )}
          />

          {ehImagem && (
            <Campo
              id="laudoTexto"
              rotulo="Laudo / conclusão"
              dica="O que o documento diz: achados e conclusão."
              erro={errors.laudoTexto?.message}
            >
              <Textarea
                id="laudoTexto"
                rows={6}
                placeholder="Achados e conclusão do laudo."
                className={CLASSE_CONTROLE}
                {...register('laudoTexto')}
              />
            </Campo>
          )}

          {mostrarValores && (
            <div className="space-y-2">
              <div>
                <p className="text-sm font-medium text-brand">Valores do exame</p>
                <p className="text-xs text-muted-foreground">
                  Cada valor ligado a um analito do catálogo entra no gráfico de evolução do
                  paciente. Sem analito, ele fica guardado no laudo.
                </p>
              </div>
              <RevisaoDeValores
                valores={valores}
                aoMudar={(novos) => {
                  setValores(novos);
                  setErrosDosValores({});
                }}
                erros={errosDosValores}
              />
            </div>
          )}

          <Campo id="observacoes" rotulo="Observação" erro={errors.observacoes?.message}>
            <Textarea
              id="observacoes"
              rows={3}
              placeholder="O que é importante lembrar sobre este exame."
              className={CLASSE_CONTROLE}
              {...register('observacoes')}
            />
          </Campo>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {documentos.enviando
                ? 'Enviando documento...'
                : documentos.lendo
                  ? 'Lendo o documento...'
                  : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Um documento da lista, com o que está acontecendo com ele. */
function LinhaDoDocumento({
  anexo,
  aoRemover,
  aoCancelarLeitura,
  aoLerDeNovo,
  podeLer,
}: {
  anexo: Anexo;
  aoRemover: () => void;
  aoCancelarLeitura: () => void;
  aoLerDeNovo: () => void;
  /** `false` quando o ambiente não tem IA: aí não se oferece "Ler de novo". */
  podeLer: boolean;
}) {
  const { arquivo, estado } = anexo;
  const Icone = arquivo.type.startsWith('image/') ? ImageIcon : FileText;

  return (
    <li className="rounded-md border border-brand/20 bg-surface-raised px-3 py-2">
      <div className="flex items-center gap-2">
        <Icone className="w-4 h-4 shrink-0 text-brand" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{arquivo.name}</span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatarTamanho(arquivo.size)}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          aria-label={`Remover ${arquivo.name}`}
          onClick={aoRemover}
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* `role="status"`: o leitor de tela anuncia a troca de "Lendo" para "Lido". */}
      <div
        role="status"
        className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 pl-6 text-xs text-muted-foreground"
      >
        {estado === 'enviando' && (
          <>
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            <span>Enviando...</span>
          </>
        )}
        {estado === 'lendo' && (
          <>
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            <span>Lendo o exame...</span>
            <button
              type="button"
              className="underline hover:text-foreground"
              onClick={aoCancelarLeitura}
            >
              Preencher à mão
            </button>
          </>
        )}
        {estado === 'lido' && (
          <>
            <CheckCircle2 className="h-3 w-3 text-emerald-600" aria-hidden="true" />
            <span>Lido — confira os campos abaixo.</span>
          </>
        )}
        {estado === 'sem-leitura' && (
          <>
            <span>{anexo.aviso ?? 'Anexado.'}</span>
            {podeLer && anexo.podeLerDeNovo && (
              <button
                type="button"
                className="underline hover:text-foreground"
                onClick={aoLerDeNovo}
              >
                Ler o documento
              </button>
            )}
          </>
        )}
      </div>
    </li>
  );
}

function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
